import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ExternalLink, Loader2, MessageSquare, Sparkles } from 'lucide-react';
import Header from './Header';
import { apiService } from '../services/api';
import type {
  ClinicalDashboardState,
  EmergingDataRow,
  EmergingIndication,
  IndicationInfo,
  Trial,
} from '../types';

const TOP_FDA_WITH_ORR = 3;
/** Max bars from the competitive table (sorted); FDA ORR comparators may append when endpoint is ORR. */
const MAX_BENCH_FROM_TABLE = 14;
const TOP_COMPETITIVE = 28;
/** Max NCCN SoC rows in the competitive table (remainder filled with emerging). */
const TOP_COMPETITIVE_SOC = 12;
/** SoC trials fetched for benchmark, competitive rows, and safety (API limit). */
const SOC_TRIALS_FETCH_LIMIT = 25;
const TOP_FDA_TABLE = 40;
/** Preload emerging trial tables for this many best-scoring similar indications (parallel fetch). */
const MAX_SIMILAR_EMERGING = 6;
/** Clinical dashboard is NCCN-only (no guideline picker). */
const CLINICAL_DASHBOARD_NCCN_TYPE = 'nccn' as const;

const COL = {
  soc: '#a78bfa',
  fda: '#34d399',
  emerging: '#fbbf24',
  nct: '#818cf8',
  card: 'rgba(30, 27, 75, 0.85)',
  border: 'rgba(139, 92, 246, 0.25)',
  gold: '#e9d5ff',
};

type CancerOpt = { id: string; name: string };
type IndicationOpt = { id: string; name: string };

type EmergingSimilar = { id: string; name: string; score: number; trialCount: number };

export interface IntegratedIndicationDashboardProps {
  variant?: 'standalone' | 'embedded';
  initialIndicationCode?: string;
  /** When true, do not read/write query-string state (e.g. `/unified-dashboard/:code` deep link). */
  disableUrlSync?: boolean;
}

type LoadDashboardOptions = {
  preferredEmergingId?: string | null;
  /** none = leave URL; push-first = new history entry; replace = replace current URL */
  urlHistory?: 'none' | 'push-first' | 'replace';
  /** When loading from the query string, must match URL key or the URL effect will re-fetch forever. */
  bootstrapUrlKey?: string;
};

function dashboardSearchString(params: {
  cancerTypeId: string;
  indicationCode: string;
  emergingId: string | null;
}): string {
  const sp = new URLSearchParams();
  sp.set('ct', params.cancerTypeId);
  sp.set('ind', params.indicationCode);
  if (params.emergingId) sp.set('em', params.emergingId);
  return sp.toString();
}

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s/+-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 2);
}

/**
 * Generic tokens that appear in almost every oncology indication — excluded from
 * both the cancer-type gate and the token-matching score to prevent false positives.
 */
const SCORE_STOPWORDS = new Set([
  'cancer', 'tumor', 'tumour', 'carcinoma', 'disease', 'cell', 'cells',
  'type', 'stage', 'advanced', 'recurrent', 'relapsed', 'refractory',
  'metastatic', 'unresectable', 'solid', 'malignant', 'malignancy',
]);

/** Too generic to use as the *only* gate for organ matching (would match NSCLC to bladder SoC). */
const GENERIC_HISTOLOGY = new Set([
  'carcinoma',
  'adenocarcinoma',
  'sarcoma',
  'melanoma',
  'lymphoma',
  'neoplasm',
  'histology',
]);

/** SoC + cancer-type text suggests urothelial / bladder — used for gate expansion + cross-site veto. */
function socLooksBladderUrothelial(socLower: string, cancerLower: string): boolean {
  const h = `${socLower} ${cancerLower}`;
  return (
    /\b(bladder|urothelial|urothelium|intravesical|bcg|nmibc|mibc|turb|tcc|tis\b|cis\b)\b/i.test(h) ||
    /\bnon[\s-]*muscle[\s-]*invasive\b/i.test(h) ||
    /\bnmit\b/i.test(h)
  );
}

function emergingHaystack(nameLower: string, otherLower: string): string {
  return `${nameLower} ${otherLower}`;
}

/** Strong signals for *other* solid tumors — veto when SoC is bladder/urothelial but emerging has no bladder signal. */
function emergingConflictsWithBladderContext(emHay: string): boolean {
  if (/\b(bladder|urothelial|nmibc|mibc|bcg|intravesical|nmit|tis\b|cis\b|turb|tcc)\b/i.test(emHay)) return false;
  if (/\bnsclc\b|\bsclc\b|\balk\b.*\bnsclc|\begfr\b.*\bnsclc|\blung\b|\bpulmonary\b/i.test(emHay)) return true;
  if (/\bcervical\b|\bcervix\b|\bovarian\b|\bendometrial\b|\buterine\b/i.test(emHay)) return true;
  return false;
}

/**
 * Tokens that must overlap emerging name / other_terms for the row to be considered (prevents
 * carcinoma-only or empty cancer-type from matching every indication).
 */
function gateTokensForSoc(socIndicationName: string, cancerTypeName: string): string[] {
  const si = socIndicationName.toLowerCase();
  const ct = cancerTypeName.toLowerCase();
  const out = new Set<string>();

  const addTok = (t: string) => {
    if (t.length > 2 && !SCORE_STOPWORDS.has(t) && !GENERIC_HISTOLOGY.has(t)) out.add(t);
  };

  for (const t of tokenize(cancerTypeName)) addTok(t);
  if (out.size === 0) {
    for (const t of tokenize(socIndicationName)) {
      if (t.length > 3 && !SCORE_STOPWORDS.has(t) && !GENERIC_HISTOLOGY.has(t)) out.add(t);
    }
  }

  // Bladder / upper tract urothelial: NMIBC, BCG, etc. often omit the word "bladder" — still same site.
  if (socLooksBladderUrothelial(si, ct)) {
    ['bladder', 'urothelial', 'nmibc', 'mibc', 'bcg', 'intravesical', 'cis', 'tis', 'nmit', 'turb', 'tcc'].forEach((x) =>
      out.add(x),
    );
  }

  return [...out];
}

function scoreEmergingIndication(
  socIndicationName: string,
  cancerTypeName: string,
  emergingName: string,
  otherTerms?: string | null,
): number {
  const en = emergingName.toLowerCase();
  const si = socIndicationName.toLowerCase();
  const ctLower = cancerTypeName.toLowerCase();
  // Normalize other_terms into a single searchable string (pipe- or comma-separated).
  const ot = otherTerms ? otherTerms.toLowerCase().replace(/[|,]/g, ' ') : '';
  const emHay = emergingHaystack(en, ot);

  if (socLooksBladderUrothelial(si, ctLower) && emergingConflictsWithBladderContext(emHay)) {
    return 0;
  }

  // Gate: overlap on specific tokens (never rely on carcinoma alone when that is all we have).
  const gateTokens = gateTokensForSoc(socIndicationName, cancerTypeName);
  if (gateTokens.length > 0 && !gateTokens.some((tok) => en.includes(tok) || ot.includes(tok))) return 0;

  const ctSpecific = tokenize(cancerTypeName).filter(
    (t) => t.length > 2 && !SCORE_STOPWORDS.has(t) && !GENERIC_HISTOLOGY.has(t),
  );

  let score = 0;

  // High-confidence: full SoC name contained in emerging name or other_terms
  if (si.length > 5 && (en.includes(si) || si.includes(en))) score += 15;
  if (ot && si.length > 5 && (ot.includes(si) || si.includes(ot.trim()))) score += 12;

  // Cancer-type specific tokens
  for (const tok of ctSpecific) {
    if (en.includes(tok)) score += 4;
    if (ot && ot.includes(tok)) score += 3;
  }

  // SoC indication-specific tokens (skip stopwords and very short tokens)
  for (const tok of tokenize(socIndicationName)) {
    if (tok.length < 4 || SCORE_STOPWORDS.has(tok)) continue;
    if (en.includes(tok)) score += 3;
    if (ot && ot.includes(tok)) score += 2;
  }

  return score;
}

function fdaSimilarityScore(
  row: Record<string, unknown>,
  socName: string,
  cancerName: string,
): number {
  const hay = [row.regimen, row.subindication, row.indication_details, row.subject, row.clin_trial_name]
    .map((x) => String(x ?? '').toLowerCase())
    .join(' | ');
  let score = 0;
  for (const needle of [socName, cancerName]) {
    if (!needle) continue;
    const n = needle.toLowerCase();
    if (hay.includes(n)) score += 8;
    for (const tok of tokenize(needle)) {
      if (tok.length < 3) continue;
      if (hay.includes(tok)) score += 2;
    }
  }
  return score;
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const s = String(v).trim();
  const m = s.match(/(\d+\.?\d*)/);
  if (m) {
    const n = parseFloat(m[1]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** ORR from emerging row or nested efficacy JSON (strings like "42%" tolerated). */
function pickEmergingOrr(row: EmergingDataRow): number | null {
  const n = numOrNull(row.orr);
  if (n != null) return n;
  const e = row.raw?.efficacy_data;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    return (
      numOrNull(o.orr_exp) ??
      numOrNull(o.orr) ??
      numOrNull(o.ORR) ??
      numOrNull(o.orr_exp_ctrl) ??
      numOrNull(o.orr_itt) ??
      numOrNull(o.confirmed_orr) ??
      numOrNull(o.investigator_orr) ??
      numOrNull(o.borr) ??
      numOrNull(o.corr)
    );
  }
  return null;
}

function pickEmergingPfs(row: EmergingDataRow): number | null {
  const n = numOrNull(row.pfs);
  if (n != null) return n;
  const e = row.raw?.efficacy_data;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    return (
      numOrNull(o.pfs_exp) ??
      numOrNull(o.mpfs_exp) ??
      numOrNull(o.median_pfs_exp) ??
      numOrNull(o.pfs) ??
      numOrNull(o.mPFS) ??
      numOrNull(o.median_pfs) ??
      numOrNull(o.mpfs) ??
      numOrNull(o.pfs_mo) ??
      numOrNull(o.pfs_months)
    );
  }
  return null;
}

function pickEmergingOs(row: EmergingDataRow): number | null {
  const n = numOrNull(row.os);
  if (n != null) return n;
  const e = row.raw?.efficacy_data;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    return (
      numOrNull(o.os_exp) ??
      numOrNull(o.mos_exp) ??
      numOrNull(o.median_os_exp) ??
      numOrNull(o.os) ??
      numOrNull(o.mOS) ??
      numOrNull(o.median_os) ??
      numOrNull(o.mos) ??
      numOrNull(o.os_mo) ??
      numOrNull(o.os_months)
    );
  }
  return null;
}

function pickEmergingG3Exp(row: EmergingDataRow): number | null {
  const s = row.raw?.safety_data;
  if (s && typeof s === 'object') {
    const o = s as Record<string, unknown>;
    return (
      numOrNull(o.g3or4_ae_rate_exp) ??
      numOrNull(o.g3_5_ae_exp) ??
      numOrNull(o.g3_ae_exp) ??
      numOrNull(o.g3or4_ae_exp) ??
      numOrNull(o.g345_ae_exp) ??
      numOrNull(o.g3_4_ae_exp) ??
      null
    );
  }
  return null;
}

function pickEmergingG5Exp(row: EmergingDataRow): number | null {
  const s = row.raw?.safety_data;
  if (s && typeof s === 'object') {
    const o = s as Record<string, unknown>;
    return (
      numOrNull(o.g5ordeath_exp) ??
      numOrNull(o.g5_death_exp) ??
      numOrNull(o.g5ordeath) ??
      numOrNull(o.g5_ae_exp) ??
      numOrNull(o.fatal_ae_exp)
    );
  }
  return null;
}

/** Normalize phase label for table (avoid bare "3" vs "Phase 3"). */
function formatPhaseLabel(phaseRaw: string): string {
  const p = phaseRaw.trim();
  if (!p || p === '—') return '—';
  if (/^phase\s+\d/i.test(p)) return p.replace(/^phase\s+/i, 'Phase ');
  if (/^phase\s*\d/i.test(p)) return p.charAt(0).toUpperCase() + p.slice(1);
  if (/^\d+(\/\d+)?$/i.test(p)) return `Phase ${p}`;
  return p;
}

function pickEmergingPhase(row: EmergingDataRow): string {
  const p = (row.phase || '').trim();
  if (p) return formatPhaseLabel(p);
  const rd = row.raw?.regimen_details;
  const td = row.raw?.trial_details;
  for (const blob of [rd, td]) {
    if (blob && typeof blob === 'object') {
      const o = blob as Record<string, unknown>;
      const ph = String(o.phase ?? o.phases ?? '').trim();
      if (ph) return formatPhaseLabel(ph);
    }
  }
  return '—';
}

function shortSocLabel(t: Trial): string {
  const d = (t.expDrug || '').trim();
  if (d) return d.length > 28 ? `${d.slice(0, 26)}…` : d;
  const n = (t.trialName || '').trim();
  if (n) return n.length > 32 ? `${n.slice(0, 30)}…` : n;
  return (t.nctId || 'SoC').toString();
}

function shortEmergingLabel(row: EmergingDataRow): string {
  const d = (row.drug_name || '').trim();
  if (d) return d.length > 26 ? `${d.slice(0, 24)}…` : d;
  return (row.study || `id ${row.id}`).toString().slice(0, 26);
}

type CompetitiveFocus =
  | { kind: 'soc'; trial: Trial }
  | { kind: 'emerging'; row: EmergingDataRow }
  | { kind: 'fda'; row: Record<string, unknown> };

type CompetitiveSortCol = 'program' | 'asset' | 'phase' | 'orr' | 'mpfs' | 'mos' | 'g35' | 'g5' | 'src';

type CompetitiveRowInternal = {
  key: string;
  program: 'soc' | 'emerging';
  asset: string;
  phase: string;
  orr: string;
  mpfs: string;
  mos: string;
  g35: string;
  g5: string;
  src: string;
  srcTone: 'nct' | 'em' | 'soc';
  _phaseN: number;
  _orrN: number | null;
  _mpfsN: number | null;
  _mosN: number | null;
  _g35N: number | null;
  _g5N: number | null;
};

function phaseRank(phase: string): number {
  const m = phase.match(/(\d+)/g);
  if (!m || m.length === 0) return 0;
  return Math.max(...m.map((x) => parseInt(x, 10) || 0));
}

function nullsLastCompare(a: number | null, b: number | null, mul: number): number {
  const aMissing = a == null;
  const bMissing = b == null;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return mul * (a - b);
}

function compareCompetitiveRows(
  a: CompetitiveRowInternal,
  b: CompetitiveRowInternal,
  col: CompetitiveSortCol,
  dir: 'asc' | 'desc',
): number {
  const mul = dir === 'asc' ? 1 : -1;
  switch (col) {
    case 'program': {
      const pa = a.program === 'soc' ? 0 : 1;
      const pb = b.program === 'soc' ? 0 : 1;
      const c = mul * (pa - pb);
      if (c !== 0) return c;
      return a.asset.localeCompare(b.asset);
    }
    case 'asset':
      return mul * a.asset.localeCompare(b.asset);
    case 'phase':
      return mul * (phaseRank(a.phase) - phaseRank(b.phase));
    case 'orr':
      return nullsLastCompare(a._orrN, b._orrN, mul);
    case 'mpfs':
      return nullsLastCompare(a._mpfsN, b._mpfsN, mul);
    case 'mos':
      return nullsLastCompare(a._mosN, b._mosN, mul);
    case 'g35':
      return nullsLastCompare(a._g35N, b._g35N, mul);
    case 'g5':
      return nullsLastCompare(a._g5N, b._g5N, mul);
    case 'src':
      return mul * a.src.localeCompare(b.src);
    default:
      return 0;
  }
}

function resolveCompetitiveFocus(
  key: string | null,
  trials: Trial[],
  rows: EmergingDataRow[],
  fdaRows: Record<string, unknown>[],
): CompetitiveFocus | null {
  if (!key) return null;
  if (key.startsWith('soc-')) {
    const id = key.slice(4);
    const trial = trials.find((t) => String(t.id) === id);
    return trial ? { kind: 'soc', trial } : null;
  }
  if (key.startsWith('e-')) {
    const id = Number.parseInt(key.slice(2), 10);
    if (!Number.isFinite(id)) return null;
    const row = rows.find((r) => r.id === id && r.type !== 'soc');
    return row ? { kind: 'emerging', row } : null;
  }
  if (key.startsWith('fda-')) {
    const id = key.slice(4);
    const row = fdaRows.find((r) => String(r.message_id ?? '') === id);
    return row ? { kind: 'fda', row } : null;
  }
  return null;
}

function extractOrrFromFda(row: Record<string, unknown>): number | null {
  const blobs = [row.endpoints, row.other_endpoints, row.regimen, row.indication_details, row.subject].map((x) =>
    String(x ?? ''),
  );
  for (const text of blobs) {
    const m =
      text.match(/ORR[:\s]+(\d+\.?\d*)\s*%/i) ||
      text.match(/objective\s+response[:\s]+(\d+\.?\d*)\s*%/i) ||
      text.match(/response\s+rate[:\s]+(\d+\.?\d*)\s*%/i);
    if (m) {
      const v = parseFloat(m[1]);
      if (Number.isFinite(v)) return v;
    }
  }
  for (const text of blobs) {
    const m = text.match(/(\d+\.?\d*)\s*%/);
    if (m) {
      const v = parseFloat(m[1]);
      if (Number.isFinite(v) && v > 0 && v <= 100) return v;
    }
  }
  return null;
}

function fdaLabel(row: Record<string, unknown>): string {
  const r = String(row.regimen ?? '').trim();
  return r.length > 36 ? `${r.slice(0, 34)}…` : r || 'FDA approval';
}

export type OrrBarRow = {
  key: string;
  label: string;
  orr: number;
  sourceLabel: string;
  fill: string;
  isLead?: boolean;
};

function benchmarkBarPayload(e: unknown): OrrBarRow | null {
  if (!e || typeof e !== 'object') return null;
  if ('key' in e && typeof (e as OrrBarRow).key === 'string' && 'orr' in e) return e as OrrBarRow;
  const pl = (e as { payload?: unknown }).payload;
  if (pl && typeof pl === 'object' && 'key' in pl && typeof (pl as OrrBarRow).key === 'string') {
    return pl as OrrBarRow;
  }
  return null;
}

function defaultSortDirForCol(c: CompetitiveSortCol): 'asc' | 'desc' {
  if (c === 'program' || c === 'asset' || c === 'src') return 'asc';
  return 'desc';
}


const IntegratedIndicationDashboard: React.FC<IntegratedIndicationDashboardProps> = ({
  variant = 'standalone',
  initialIndicationCode,
  disableUrlSync = false,
}) => {
  const embedded = variant === 'embedded';
  const syncUrlAllowed = !disableUrlSync;
  const basePath = '/unified-dashboard';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const serverHydrateAttemptedRef = useRef(false);
  const lastBootstrappedQueryRef = useRef('');
  const pathBootstrapDoneRef = useRef(false);
  const urlBootstrapInFlightRef = useRef(false);
  /** When set, show resume dialog instead of silently pushing saved ?ind= into the URL (login / return visit). */
  const [resumePrompt, setResumePrompt] = useState<null | { saved: ClinicalDashboardState; label: string }>(null);

  const [cancerTypes, setCancerTypes] = useState<CancerOpt[]>([]);
  const [indications, setIndications] = useState<IndicationOpt[]>([]);
  const [selectedCancerType, setSelectedCancerType] = useState('');

  const selectedCancerTypeRef = useRef(selectedCancerType);
  selectedCancerTypeRef.current = selectedCancerType;
  const cancerTypesRef = useRef(cancerTypes);
  cancerTypesRef.current = cancerTypes;
  const [selectedIndication, setSelectedIndication] = useState('');

  const [step, setStep] = useState<'form' | 'results'>(() =>
    initialIndicationCode && disableUrlSync ? 'results' : 'form',
  );

  const [loading, setLoading] = useState(() => Boolean(initialIndicationCode && disableUrlSync));
  const [loadingIndications, setLoadingIndications] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [indicationInfo, setIndicationInfo] = useState<IndicationInfo | null>(null);
  const [socTrials, setSocTrials] = useState<Trial[]>([]);
  const [emergingAlternatives, setEmergingAlternatives] = useState<EmergingSimilar[]>([]);
  const [activeEmergingId, setActiveEmergingId] = useState<string | null>(null);
  const [emergingByIndicationId, setEmergingByIndicationId] = useState<Record<string, EmergingDataRow[]>>({});
  const [fdaRows, setFdaRows] = useState<Record<string, unknown>[]>([]);
  /** Row key: `soc-`, `e-`, or benchmark-only `fda-` — drives Asset focus. */
  const [competitiveFocusKey, setCompetitiveFocusKey] = useState<string | null>(null);
  const [competitiveSort, setCompetitiveSort] = useState<{ col: CompetitiveSortCol; dir: 'asc' | 'desc' }>({
    col: 'program',
    dir: 'asc',
  });

  const cancerTypeName = useMemo(
    () => cancerTypes.find((c) => c.id === selectedCancerType)?.name || '',
    [cancerTypes, selectedCancerType],
  );

  const emergingRows = useMemo(() => {
    if (!activeEmergingId) return [];
    return emergingByIndicationId[activeEmergingId] ?? [];
  }, [activeEmergingId, emergingByIndicationId]);

  // Lazy-load emerging data when the user switches to an indication not yet fetched.
  useEffect(() => {
    if (!activeEmergingId) return;
    if (emergingByIndicationId[activeEmergingId] !== undefined) return;
    (async () => {
      try {
        const em = await apiService.fetchEmergingData(activeEmergingId, { limit: 30 });
        const rows = em.success && Array.isArray(em.data) ? em.data : [];
        setEmergingByIndicationId((prev) => ({ ...prev, [activeEmergingId]: rows }));
      } catch {
        setEmergingByIndicationId((prev) => ({ ...prev, [activeEmergingId]: [] }));
      }
    })();
  }, [activeEmergingId, emergingByIndicationId]);

  useEffect(() => {
    (async () => {
      try {
        setCancerTypes(await apiService.fetchCancerTypes());
      } catch {
        setCancerTypes([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedCancerType) {
      setIndications([]);
      setSelectedIndication('');
      return;
    }
    (async () => {
      try {
        setLoadingIndications(true);
        setIndications(await apiService.fetchIndications(selectedCancerType));
      } catch {
        setIndications([]);
      } finally {
        setLoadingIndications(false);
      }
    })();
  }, [selectedCancerType]);

  const loadDashboard = useCallback(
    async (indicationCode: string, options?: LoadDashboardOptions) => {
      const urlHistory = options?.urlHistory ?? 'none';
      const nccnType = CLINICAL_DASHBOARD_NCCN_TYPE;
      setSelectedIndication(indicationCode);
      setLoading(true);
      setError(null);
      try {
        // Phase 1 (parallel): indication info + emerging list don't depend on each other.
        const [indInfo, emergingList] = await Promise.all([
          apiService.fetchIndicationInfo({ indication_code: indicationCode, nccn_type: nccnType }),
          apiService.fetchEmergingIndications(),
        ]);
        const nccnVersion = indInfo.nccnVersion?.trim() || '2.2025';
        setIndicationInfo({ ...indInfo, nccnVersion });

        // Score emerging indications (CPU-only, instant).
        const socName = indInfo.name || '';
        const ctName = indInfo.cancerType || '';
        const byId = new Map<string, EmergingSimilar>();
        for (const ind of emergingList as EmergingIndication[]) {
          const sc = scoreEmergingIndication(socName, ctName, ind.name, ind.other_terms);
          if (sc < 5) continue;
          const prev = byId.get(ind.id);
          const next: EmergingSimilar = {
            id: ind.id,
            name: ind.name,
            score: sc,
            trialCount: typeof ind.trial_count === 'number' ? ind.trial_count : 0,
          };
          if (!prev || sc > prev.score) byId.set(ind.id, next);
        }
        // DB-curated links (soc_emerging_indication_link): always surface even when fuzzy score is low.
        for (const link of indInfo.manualEmergingLinks ?? []) {
          const lid = String(link.emergingIndicationId || '').trim();
          if (!lid) continue;
          const ind = (emergingList as EmergingIndication[]).find((x) => String(x.id) === lid);
          if (!ind) continue;
          const boost = 8000 - Math.min(7990, Math.max(0, Number(link.priority) || 0));
          const prev = byId.get(lid);
          const trialCount = typeof ind.trial_count === 'number' ? ind.trial_count : 0;
          if (!prev || boost > prev.score) {
            byId.set(lid, { id: lid, name: ind.name, score: boost, trialCount });
          }
        }
        const ranked = [...byId.values()]
          .sort((a, b) => b.score - a.score)
          .slice(0, MAX_SIMILAR_EMERGING);
        console.group(`[Dashboard] Emerging match for "${socName}" / "${ctName}"`);
        if (ranked.length === 0) {
          console.log('No matches above threshold (score ≥ 5). Total indications scored:', emergingList.length);
        } else {
          console.table(ranked.map((r) => ({ id: r.id, name: r.name, score: r.score, trials: r.trialCount })));
        }
        console.groupEnd();
        setEmergingAlternatives(ranked);
        const firstId = ranked[0]?.id ?? null;
        const pref = (options?.preferredEmergingId || '').trim();
        const chosenEmerging = pref && ranked.some((r) => r.id === pref) ? pref : firstId;
        if (chosenEmerging) {
          const chosen = ranked.find((r) => r.id === chosenEmerging);
          console.log(`[Dashboard] Selected emerging: "${chosen?.name}" (score ${chosen?.score}, id ${chosenEmerging})`);
        }
        setActiveEmergingId(chosenEmerging);

        // Phase 2 (parallel): trials, FDA, and the chosen emerging data all at once.
        const [trialsResult, fdaRes, emRes] = await Promise.all([
          apiService.fetchTrials({
            indication_code: indicationCode,
            nccn_type: nccnType,
            nccn_version: nccnVersion,
            limit: SOC_TRIALS_FETCH_LIMIT,
          }),
          apiService.fetchFDAApprovals(5).catch(() => null),
          chosenEmerging
            ? apiService.fetchEmergingData(chosenEmerging, { limit: 30 }).catch(() => null)
            : Promise.resolve(null),
        ]);

        setSocTrials(trialsResult?.trials ?? []);

        const initialLoaded: [string, EmergingDataRow[]][] = [];
        if (chosenEmerging && emRes) {
          initialLoaded.push([chosenEmerging, emRes.success && Array.isArray(emRes.data) ? emRes.data : []]);
        } else if (chosenEmerging) {
          initialLoaded.push([chosenEmerging, []]);
        }
        setEmergingByIndicationId(Object.fromEntries(initialLoaded));

        if (fdaRes?.success && Array.isArray(fdaRes.data)) {
            const socN = indInfo.name || '';
            const ctN = indInfo.cancerType || '';
            const scored = (fdaRes.data as Record<string, unknown>[]).map((row) => ({
              row,
              score: fdaSimilarityScore(row, socN, ctN),
            }));
            scored.sort((a, b) => b.score - a.score);
            setFdaRows(scored.filter((x) => x.score > 0).slice(0, TOP_FDA_TABLE).map((x) => x.row));
          } else {
            setFdaRows([]);
          }

        const cancerSave =
          String(indInfo.cancerType || '').trim() || String(selectedCancerTypeRef.current || '').trim();
        if (cancerSave) {
          void apiService
            .saveClinicalDashboardState({
              cancer_type_id: cancerSave,
              indication_code: indicationCode,
              nccn_type: nccnType,
              active_emerging_id: chosenEmerging,
            })
            .catch(() => {});
        }
        if (syncUrlAllowed && cancerSave) {
          const qs = dashboardSearchString({
            cancerTypeId: cancerSave,
            indicationCode: indicationCode,
            emergingId: chosenEmerging,
          });
          if (urlHistory === 'push-first') {
            navigate({ pathname: basePath, search: qs }, { replace: false });
          } else if (urlHistory === 'replace') {
            setSearchParams(new URLSearchParams(qs), { replace: true });
          }
        }

        if (options?.bootstrapUrlKey !== undefined) {
          lastBootstrappedQueryRef.current = options.bootstrapUrlKey;
        } else if (cancerSave) {
          lastBootstrappedQueryRef.current = `${indicationCode}|${cancerSave}|${chosenEmerging || ''}`;
        }

        setStep('results');
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } }; message?: string };
        setError(err?.response?.data?.error || err?.message || 'Failed to load dashboard');
        setSocTrials([]);
        setEmergingAlternatives([]);
        setActiveEmergingId(null);
        setEmergingByIndicationId({});
        setFdaRows([]);
        lastBootstrappedQueryRef.current = '';
        setStep('form');
      } finally {
        setLoading(false);
      }
    },
    [syncUrlAllowed, navigate, setSearchParams, basePath],
  );

  /** Deep link `/unified-dashboard/:ind` — no query-string sync. */
  useEffect(() => {
    if (!disableUrlSync || !initialIndicationCode) return;
    if (pathBootstrapDoneRef.current) return;
    pathBootstrapDoneRef.current = true;
    (async () => {
      try {
        const info = await apiService.fetchIndicationInfo({
          indication_code: initialIndicationCode,
          nccn_type: CLINICAL_DASHBOARD_NCCN_TYPE,
        });
        const cancerId = String(info.cancerType || '').trim();
        setSelectedCancerType(cancerId);
        setSelectedIndication(initialIndicationCode);
        setStep('results');
        await loadDashboard(initialIndicationCode, { urlHistory: 'none' });
      } catch {
        pathBootstrapDoneRef.current = false;
        setStep('form');
      }
    })();
  }, [disableUrlSync, initialIndicationCode, loadDashboard]);

  /** Load server-saved dashboard once; ask before mutating URL so users can pick a new indication. */
  useEffect(() => {
    if (!syncUrlAllowed || cancerTypesRef.current.length === 0) return;
    if (searchParams.get('ind')) {
      serverHydrateAttemptedRef.current = true;
      setResumePrompt(null);
      return;
    }
    if (serverHydrateAttemptedRef.current) return;
    serverHydrateAttemptedRef.current = true;
    (async () => {
      try {
        const saved = await apiService.fetchClinicalDashboardState();
        if (new URLSearchParams(window.location.search).get('ind')) return;
        if (
          saved?.indication_code &&
          saved?.cancer_type_id &&
          cancerTypesRef.current.some((c) => c.id === saved.cancer_type_id)
        ) {
          let label = saved.indication_code;
          try {
            const info = await apiService.fetchIndicationInfo({
              indication_code: saved.indication_code,
              nccn_type: CLINICAL_DASHBOARD_NCCN_TYPE,
            });
            label = (info.name && String(info.name).trim()) || label;
          } catch {
            /* keep code as label */
          }
          setResumePrompt({ saved, label });
        }
      } catch {
        /* ignore */
      }
    })();
  }, [syncUrlAllowed, cancerTypes.length, searchParams, setSearchParams]);

  const handleResumeContinue = useCallback(() => {
    if (!resumePrompt) return;
    const { saved } = resumePrompt;
    const qs = dashboardSearchString({
      cancerTypeId: saved.cancer_type_id,
      indicationCode: saved.indication_code,
      emergingId: saved.active_emerging_id || null,
    });
    setResumePrompt(null);
    setSearchParams(new URLSearchParams(qs), { replace: true });
  }, [resumePrompt, setSearchParams]);

  const handleResumeNewSearch = useCallback(() => {
    setResumePrompt(null);
    void apiService.clearClinicalDashboardState().catch(() => {});
  }, []);

  /** Load dashboard from `?ct=&ind=&em=` */
  useEffect(() => {
    if (cancerTypesRef.current.length === 0) return;
    const ind = searchParams.get('ind');
    const ct = searchParams.get('ct') || '';
    const em = searchParams.get('em') || '';
    if (!ind) return;

    const nccn = CLINICAL_DASHBOARD_NCCN_TYPE;
    const key = `${ind}|${ct}|${em}`;
    if (lastBootstrappedQueryRef.current === key) return;
    if (urlBootstrapInFlightRef.current) return;
    urlBootstrapInFlightRef.current = true;

    (async () => {
      try {
        let cancerId = ct;
        if (!cancerId) {
          try {
            const info = await apiService.fetchIndicationInfo({ indication_code: ind, nccn_type: nccn });
            cancerId = String(info.cancerType || '').trim();
          } catch {
            lastBootstrappedQueryRef.current = '';
            return;
          }
        }
        if (!cancerId || !cancerTypesRef.current.some((c) => c.id === cancerId)) {
          lastBootstrappedQueryRef.current = '';
          return;
        }
        setSelectedCancerType(cancerId);
        setSelectedIndication(ind);
        setStep('results');
        await loadDashboard(ind, {
          preferredEmergingId: em || null,
          urlHistory: 'none',
          bootstrapUrlKey: key,
        });
      } finally {
        urlBootstrapInFlightRef.current = false;
      }
    })();
  }, [cancerTypes.length, searchParams, loadDashboard]);

  /** Browser back from results URL → form without re-hydrating from server. */
  useEffect(() => {
    if (!syncUrlAllowed || loading) return;
    if (searchParams.get('ind')) return;
    if (step !== 'results') return;
    lastBootstrappedQueryRef.current = '';
    setStep('form');
    setSocTrials([]);
    setEmergingAlternatives([]);
    setActiveEmergingId(null);
    setEmergingByIndicationId({});
    setFdaRows([]);
    setIndicationInfo(null);
  }, [syncUrlAllowed, searchParams, step, loading]);

  /** Keep server + query string in sync when switching similar emerging indication (or when results finish loading). */
  useEffect(() => {
    if (step !== 'results' || loading) return;
    if (!selectedIndication) return;
    const cancerSave = String(indicationInfo?.cancerType || selectedCancerType || '').trim();
    if (!cancerSave) return;
    const nextKey = `${selectedIndication}|${cancerSave}|${activeEmergingId || ''}`;
    if (lastBootstrappedQueryRef.current === nextKey) return;
    lastBootstrappedQueryRef.current = nextKey;
    if (syncUrlAllowed) {
      const qs = dashboardSearchString({
        cancerTypeId: cancerSave,
        indicationCode: selectedIndication,
        emergingId: activeEmergingId,
      });
      setSearchParams(new URLSearchParams(qs), { replace: true });
    }
    void apiService
      .saveClinicalDashboardState({
        cancer_type_id: cancerSave,
        indication_code: selectedIndication,
        nccn_type: CLINICAL_DASHBOARD_NCCN_TYPE,
        active_emerging_id: activeEmergingId,
      })
      .catch(() => {});
  }, [
    syncUrlAllowed,
    step,
    loading,
    activeEmergingId,
    selectedIndication,
    selectedCancerType,
    indicationInfo?.cancerType,
    setSearchParams,
  ]);

  const fdaDisplay = useMemo(() => {
    const socName = indicationInfo?.name || '';
    const ct = indicationInfo?.cancerType || cancerTypeName;
    return fdaRows.map((row) => ({
      row,
      score: fdaSimilarityScore(row, socName, ct),
      orr: extractOrrFromFda(row),
    }));
  }, [fdaRows, indicationInfo, cancerTypeName]);

  const [benchMetric, setBenchMetric] = useState<'orr' | 'mpfs' | 'mos'>('orr');

  const selectBenchMetric = useCallback((m: 'orr' | 'mpfs' | 'mos') => {
    setBenchMetric(m);
    const sortCol: CompetitiveSortCol = m === 'orr' ? 'orr' : m === 'mpfs' ? 'mpfs' : 'mos';
    setCompetitiveSort({ col: sortCol, dir: 'desc' });
  }, []);

  const competitiveRows = useMemo((): CompetitiveRowInternal[] => {
    const rows: CompetitiveRowInternal[] = [];

    const socCap = Math.min(TOP_COMPETITIVE_SOC, socTrials.length, TOP_COMPETITIVE);
    for (const t of socTrials.slice(0, socCap)) {
      const ph = formatPhaseLabel((t.phase || '').trim());
      const orrN = t.orrExp != null && Number.isFinite(Number(t.orrExp)) ? Number(t.orrExp) : null;
      const mpfsN = t.mPFSExp != null && Number.isFinite(Number(t.mPFSExp)) ? Number(t.mPFSExp) : null;
      const mosN = t.mOSExp != null && Number.isFinite(Number(t.mOSExp)) ? Number(t.mOSExp) : null;
      const g35N = t.g35AEExp != null && Number.isFinite(Number(t.g35AEExp)) ? Number(t.g35AEExp) : null;
      const g5N = t.g5DeathExp != null && Number.isFinite(Number(t.g5DeathExp)) ? Number(t.g5DeathExp) : null;
      rows.push({
        key: `soc-${t.id}`,
        program: 'soc',
        asset: shortSocLabel(t),
        phase: ph,
        orr: orrN != null ? `${orrN}%` : '—',
        mpfs: mpfsN != null ? `${mpfsN}mo` : '—',
        mos: mosN != null ? `${mosN}mo` : '—',
        g35: g35N != null ? `${g35N}%` : '—',
        g5: g5N != null ? `${g5N}%` : '—',
        src: 'NCCN',
        srcTone: 'soc',
        _phaseN: phaseRank(ph),
        _orrN: orrN,
        _mpfsN: mpfsN,
        _mosN: mosN,
        _g35N: g35N,
        _g5N: g5N,
      });
    }

    for (const r of emergingRows.filter((e) => e.type !== 'soc')) {
      if (rows.length >= TOP_COMPETITIVE) break;
      const phStr = pickEmergingPhase(r);
      const orr = pickEmergingOrr(r);
      const pfs = pickEmergingPfs(r);
      const os = pickEmergingOs(r);
      const g35v = pickEmergingG3Exp(r);
      const g5v = pickEmergingG5Exp(r);
      rows.push({
        key: `e-${r.id}`,
        program: 'emerging',
        asset: shortEmergingLabel(r),
        phase: phStr,
        orr: orr != null ? `${orr}%` : '—',
        mpfs: pfs != null ? `${pfs}mo` : '—',
        mos: os != null ? `${os}mo` : '—',
        g35: g35v != null ? `${g35v}%` : '—',
        g5: g5v != null ? `${g5v}%` : '—',
        src: (r.nct || '').trim() ? 'NCT' : 'Emrg',
        srcTone: (r.nct || '').trim() ? 'nct' : 'em',
        _phaseN: phaseRank(phStr),
        _orrN: orr,
        _mpfsN: pfs,
        _mosN: os,
        _g35N: g35v,
        _g5N: g5v,
      });
    }

    const { col, dir } = competitiveSort;
    return [...rows].sort((a, b) => compareCompetitiveRows(a, b, col, dir));
  }, [emergingRows, socTrials, competitiveSort]);

  const orrBenchmark = useMemo((): OrrBarRow[] => {
    const pickBench = (r: CompetitiveRowInternal): number | null => {
      if (benchMetric === 'mpfs') return r._mpfsN;
      if (benchMetric === 'mos') return r._mosN;
      return r._orrN;
    };

    const out: OrrBarRow[] = [];
    const chartKeys = new Set<string>();

    for (const r of competitiveRows) {
      const v = pickBench(r);
      if (v == null) continue;
      const fill =
        r.program === 'soc' ? COL.soc : r.srcTone === 'nct' ? COL.nct : COL.emerging;
      const srcLabel = r.program === 'soc' ? 'SoC DB' : r.srcTone === 'nct' ? 'NCT' : 'Emerging';
      out.push({
        key: r.key,
        label: r.asset.length > 38 ? `${r.asset.slice(0, 36)}…` : r.asset,
        orr: v,
        sourceLabel: srcLabel,
        fill,
      });
      chartKeys.add(r.key);
      if (out.length >= MAX_BENCH_FROM_TABLE) break;
    }

    if (benchMetric === 'orr') {
      const fdaWithOrr = fdaDisplay
        .filter((x) => x.orr != null)
        .slice(0, TOP_FDA_WITH_ORR * 2)
        .sort((a, b) => (a.orr as number) - (b.orr as number))
        .slice(0, TOP_FDA_WITH_ORR);
      for (const x of fdaWithOrr) {
        const k = `fda-${String(x.row.message_id ?? '')}`;
        if (!k || k === 'fda-' || chartKeys.has(k)) continue;
        chartKeys.add(k);
        out.push({
          key: k,
          label: fdaLabel(x.row),
          orr: x.orr as number,
          sourceLabel: 'FDA',
          fill: COL.fda,
        });
      }
    }

    return out;
  }, [competitiveRows, fdaDisplay, benchMetric]);

  const competitiveFocus = useMemo(
    () => resolveCompetitiveFocus(competitiveFocusKey, socTrials, emergingRows, fdaRows),
    [competitiveFocusKey, socTrials, emergingRows, fdaRows],
  );

  useEffect(() => {
    if (step !== 'results' || loading) return;
    setCompetitiveFocusKey((prev) => {
      const valid = new Set([
        ...competitiveRows.map((r) => r.key),
        ...orrBenchmark.map((b) => b.key),
      ]);
      if (prev && valid.has(prev)) return prev;
      return competitiveRows[0]?.key ?? null;
    });
  }, [step, loading, competitiveRows, orrBenchmark]);

  const toggleCompetitiveSort = useCallback((col: CompetitiveSortCol) => {
    setCompetitiveSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: defaultSortDirForCol(col) },
    );
  }, []);

  const onBenchmarkBarClick = useCallback((data: unknown, _index?: number, _e?: unknown) => {
    const row = benchmarkBarPayload(data);
    if (row?.key) setCompetitiveFocusKey(row.key);
  }, []);

  const safetyRows = useMemo(() => {
    type SafetyEntry = { label: string; val: number; signal: 'LOW' | 'WATCH' | 'FLAG' };
    const g35Entries: SafetyEntry[] = [];
    const g5Entries: SafetyEntry[] = [];

    for (const t of socTrials) {
      const lbl = shortSocLabel(t);
      if (t.g35AEExp != null) {
        const v = Number(t.g35AEExp);
        g35Entries.push({ label: lbl, val: v, signal: v > 50 ? 'FLAG' : v > 30 ? 'WATCH' : 'LOW' });
      }
      if (t.g5DeathExp != null) {
        const v = Number(t.g5DeathExp);
        g5Entries.push({ label: lbl, val: v, signal: v > 5 ? 'FLAG' : v > 2 ? 'WATCH' : 'LOW' });
      }
    }
    for (const r of emergingRows.filter((e) => e.type !== 'soc')) {
      const lbl = shortEmergingLabel(r);
      const g35v = pickEmergingG3Exp(r);
      const g5v = pickEmergingG5Exp(r);
      if (g35v != null) g35Entries.push({ label: lbl, val: g35v, signal: g35v > 50 ? 'FLAG' : g35v > 30 ? 'WATCH' : 'LOW' });
      if (g5v != null) g5Entries.push({ label: lbl, val: g5v, signal: g5v > 5 ? 'FLAG' : g5v > 2 ? 'WATCH' : 'LOW' });
    }

    return {
      g35: g35Entries.sort((a, b) => b.val - a.val).slice(0, 5),
      g5: g5Entries.sort((a, b) => b.val - a.val).slice(0, 5),
    };
  }, [socTrials, emergingRows]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIndication) {
      setError('Select cancer type and indication.');
      return;
    }
    await loadDashboard(selectedIndication, { urlHistory: 'push-first' });
  };

  const resetForm = () => {
    setResumePrompt(null);
    void apiService.clearClinicalDashboardState().catch(() => {});
    if (syncUrlAllowed) {
      navigate(basePath, { replace: true });
    }
    serverHydrateAttemptedRef.current = false;
    lastBootstrappedQueryRef.current = '';
    setStep('form');
    setSocTrials([]);
    setEmergingAlternatives([]);
    setActiveEmergingId(null);
    setEmergingByIndicationId({});
    setFdaRows([]);
    setIndicationInfo(null);
    setError(null);
  };


  const formBlock = (
    <section
      className={`max-w-xl mx-auto rounded-2xl border p-6 sm:p-8 ${
        embedded ? 'border-violet-500/30 bg-[#151032]' : 'border-slate-200 bg-white'
      }`}
    >
      <h2 className={`text-lg font-semibold ${embedded ? 'text-amber-100/95' : 'text-slate-900'}`}>
        Select indication
      </h2>
      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        {[
          {
            label: 'Cancer type',
            node: (
              <select
                value={selectedCancerType}
                onChange={(e) => {
                  setSelectedCancerType(e.target.value);
                  setSelectedIndication('');
                }}
                className={`w-full rounded-xl border px-3 py-2 text-sm ${
                  embedded
                    ? 'border-violet-500/30 bg-[#0c0a1a] text-slate-100'
                    : 'border-slate-200 bg-white text-slate-900'
                }`}
                required
              >
                <option value="">Choose…</option>
                {cancerTypes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: 'Indication',
            node: (
              <select
                value={selectedIndication}
                onChange={(e) => setSelectedIndication(e.target.value)}
                className={`w-full rounded-xl border px-3 py-2 text-sm ${
                  embedded
                    ? 'border-violet-500/30 bg-[#0c0a1a] text-slate-100'
                    : 'border-slate-200 bg-white text-slate-900'
                }`}
                required
                disabled={!selectedCancerType || loadingIndications}
              >
                <option value="">{loadingIndications ? 'Loading…' : 'Choose…'}</option>
                {indications.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            ),
          },
        ].map((f) => (
          <label key={f.label} className="block space-y-1.5 text-sm">
            <span
              className={`text-xs font-semibold uppercase tracking-wide ${
                embedded ? 'text-violet-300/80' : 'text-slate-500'
              }`}
            >
              {f.label}
            </span>
            {f.node}
          </label>
        ))}
        {error && step === 'form' && (
          <p className={`text-sm ${embedded ? 'text-rose-300' : 'text-rose-600'}`}>{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex justify-center items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-3 text-sm font-bold text-slate-950 hover:from-amber-400 hover:to-amber-500 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          View dashboard
        </button>
      </form>
    </section>
  );

  const navPills = (
    <nav className="flex flex-wrap gap-2 text-xs font-semibold">
      <span className="rounded-full bg-violet-500/30 px-3 py-1.5 text-violet-100 border border-violet-400/40">
        Overview
      </span>
      <Link
        to="/fda-approvals"
        className="rounded-full border border-white/10 px-3 py-1.5 text-slate-300 hover:bg-white/5 hover:text-white"
      >
        FDA Approvals
      </Link>
      <Link to="/chatbot" className="rounded-full border border-white/10 px-3 py-1.5 text-slate-300 hover:bg-white/5">
        KOL / Chat
      </Link>
      <Link to="/ongoing-trials" className="rounded-full border border-white/10 px-3 py-1.5 text-slate-300 hover:bg-white/5">
        Ongoing Trials
      </Link>
      <Link to="/reports" className="rounded-full border border-white/10 px-3 py-1.5 text-slate-300 hover:bg-white/5">
        SoC Reports
      </Link>
    </nav>
  );

  const resultsView = !loading && step === 'results' && (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-violet-500/20 pb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200/90">Ruby Oncology Insights</p>
          <h2 className="mt-1 text-lg sm:text-xl font-bold text-white">
            {indicationInfo?.name || 'Indication'}
            {cancerTypeName ? (
              <span className="text-amber-100/80 font-normal"> · {cancerTypeName}</span>
            ) : null}
          </h2>
          <p className="text-xs text-violet-200/70 mt-1">NCCN · v{indicationInfo?.nccnVersion}</p>
        </div>
        {navPills}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={resetForm}
            className="rounded-xl border border-violet-500/40 bg-violet-950/50 px-3 py-2 text-xs font-semibold text-violet-100 hover:bg-violet-900/60"
          >
            Change indication
          </button>
          <Link
            to={`/reports/${selectedIndication}/${CLINICAL_DASHBOARD_NCCN_TYPE}`}
            className="inline-flex items-center gap-1 rounded-xl border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100 hover:bg-amber-500/20"
          >
            Full SoC report <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-950/40 px-4 py-3 text-sm text-rose-200">{error}</div>
      )}

      {emergingAlternatives.length > 0 && (
        <div
          className="rounded-xl border px-3 py-2"
          style={{ background: 'rgba(30, 27, 75, 0.55)', borderColor: COL.border }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-h-[40px]">
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-200/90">
              Similar emerging
            </span>
            <div className="flex flex-nowrap gap-2 overflow-x-auto pb-0.5 -mx-1 px-1">
              {emergingAlternatives.map((alt) => {
                const active = alt.id === activeEmergingId;
                const nRows = (emergingByIndicationId[alt.id] ?? []).filter((r) => r.type !== 'soc').length;
                return (
                  <button
                    key={alt.id}
                    type="button"
                    title={alt.name}
                    onClick={() => setActiveEmergingId(alt.id)}
                    className={`shrink-0 rounded-lg border px-2.5 py-1.5 text-left transition-colors max-w-[220px] ${
                      active
                        ? 'border-amber-400/50 bg-amber-500/15 text-amber-50 ring-1 ring-amber-400/25'
                        : 'border-white/10 bg-[#0c0a1a]/50 text-slate-300 hover:border-violet-400/30'
                    }`}
                  >
                    <span className="block text-[11px] font-semibold leading-snug line-clamp-2">{alt.name}</span>
                    <span className="mt-0.5 block text-[9px] text-slate-500 tabular-nums">
                      {alt.score} · {nRows} rows
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
        {/* Left: asset focus + AI + FDA (desktop column order places this on the left) */}
        <aside className="xl:col-span-4 space-y-4 order-2 xl:order-1">
          <div
            className="rounded-2xl border p-5 space-y-3"
            style={{ background: COL.card, borderColor: COL.border }}
          >
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-200/90">Asset focus</h3>
            <p className="text-[11px] text-slate-500 leading-snug">
              Select a row in <span className="text-slate-400">Competitive landscape</span> or a{' '}
              <span className="text-slate-400">benchmark</span> bar for detail.
            </p>
            {!competitiveFocus ? (
              <p className="text-sm text-slate-500">Nothing selected yet.</p>
            ) : competitiveFocus.kind === 'soc' ? (
              <dl className="space-y-2 text-sm text-slate-200">
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400 shrink-0">Context</dt>
                  <dd className="font-medium text-right text-slate-400 text-xs">{indicationInfo?.name || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Regimen</dt>
                  <dd className="font-medium text-right text-slate-100 max-w-[min(100%,14rem)] break-words">
                    {(competitiveFocus.trial.expDrug || '').trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Study</dt>
                  <dd className="text-right text-xs max-w-[min(100%,14rem)] break-words">
                    {(competitiveFocus.trial.trialName || '').trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">NCT</dt>
                  <dd className="text-right font-mono text-xs">
                    {(competitiveFocus.trial.nctId || '').trim() ? (
                      <a
                        href={`https://clinicaltrials.gov/study/${encodeURIComponent((competitiveFocus.trial.nctId || '').trim())}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-amber-200/90 hover:underline"
                      >
                        {competitiveFocus.trial.nctId}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Phase</dt>
                  <dd className="text-right">{formatPhaseLabel((competitiveFocus.trial.phase || '').trim())}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">NCCN</dt>
                  <dd className="text-right text-xs">{competitiveFocus.trial.preferredStatus || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">ORR / ctrl</dt>
                  <dd className="text-right tabular-nums">
                    {competitiveFocus.trial.orrExp != null ? `${competitiveFocus.trial.orrExp}%` : '—'}
                    <span className="text-slate-500"> · </span>
                    {competitiveFocus.trial.orrCtrl != null ? `${competitiveFocus.trial.orrCtrl}%` : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">mPFS / mOS</dt>
                  <dd className="text-right tabular-nums text-xs">
                    {competitiveFocus.trial.mPFSExp != null ? `${competitiveFocus.trial.mPFSExp} mo` : '—'}
                    <span className="text-slate-500"> · </span>
                    {competitiveFocus.trial.mOSExp != null ? `${competitiveFocus.trial.mOSExp} mo` : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">N (exp)</dt>
                  <dd className="text-right">{competitiveFocus.trial.expN ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Primary EP</dt>
                  <dd className="text-right text-[11px] text-slate-300 max-h-24 overflow-y-auto">
                    {(competitiveFocus.trial.primaryEndpoint || '').trim().slice(0, 280) || '—'}
                  </dd>
                </div>
              </dl>
            ) : competitiveFocus.kind === 'fda' ? (
              <dl className="space-y-2 text-sm text-slate-200">
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400 shrink-0">Context</dt>
                  <dd className="font-medium text-right text-slate-400 text-xs">{indicationInfo?.name || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Source</dt>
                  <dd className="text-right text-xs font-semibold text-emerald-200/90">FDA — similar</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Regimen</dt>
                  <dd className="font-medium text-right text-slate-100 max-w-[min(100%,14rem)] break-words">
                    {fdaLabel(competitiveFocus.row)}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Parsed ORR</dt>
                  <dd className="text-right tabular-nums">
                    {extractOrrFromFda(competitiveFocus.row) != null
                      ? `${extractOrrFromFda(competitiveFocus.row)}%`
                      : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Notes</dt>
                  <dd className="text-right text-[11px] text-slate-400 max-h-28 overflow-y-auto break-words">
                    {String(competitiveFocus.row.indication_details ?? competitiveFocus.row.subject ?? '').trim().slice(0, 400) || '—'}
                  </dd>
                </div>
              </dl>
            ) : (
              <dl className="space-y-2 text-sm text-slate-200">
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400 shrink-0">Context</dt>
                  <dd className="font-medium text-right text-slate-400 text-xs">{indicationInfo?.name || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Asset</dt>
                  <dd className="font-medium text-right text-slate-100 max-w-[min(100%,14rem)] break-words">
                    {(competitiveFocus.row.drug_name || '').trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Company</dt>
                  <dd className="text-right text-xs break-words">{(competitiveFocus.row.company || '').trim() || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Study</dt>
                  <dd className="text-right text-xs max-w-[min(100%,14rem)] break-words">
                    {(competitiveFocus.row.study || '').trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">NCT</dt>
                  <dd className="text-right font-mono text-xs">
                    {(competitiveFocus.row.nct || '').trim() ? (
                      <a
                        href={`https://clinicaltrials.gov/study/${encodeURIComponent((competitiveFocus.row.nct || '').trim())}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-amber-200/90 hover:underline"
                      >
                        {competitiveFocus.row.nct}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Phase</dt>
                  <dd className="text-right">{pickEmergingPhase(competitiveFocus.row)}</dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">Population</dt>
                  <dd className="text-right text-[11px] text-slate-300 max-h-20 overflow-y-auto">
                    {(competitiveFocus.row.patient_population || '').trim().slice(0, 220) || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">ORR / mPFS / mOS</dt>
                  <dd className="text-right tabular-nums text-xs">
                    {pickEmergingOrr(competitiveFocus.row) != null ? `${pickEmergingOrr(competitiveFocus.row)}%` : '—'}
                    <span className="text-slate-500"> · </span>
                    {pickEmergingPfs(competitiveFocus.row) != null ? `${pickEmergingPfs(competitiveFocus.row)} mo` : '—'}
                    <span className="text-slate-500"> · </span>
                    {pickEmergingOs(competitiveFocus.row) != null ? `${pickEmergingOs(competitiveFocus.row)} mo` : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                  <dt className="text-slate-400">G3–5 / G5 exp %</dt>
                  <dd className="text-right tabular-nums text-xs">
                    {pickEmergingG3Exp(competitiveFocus.row) != null ? `${pickEmergingG3Exp(competitiveFocus.row)}%` : '—'}
                    <span className="text-slate-500"> · </span>
                    {pickEmergingG5Exp(competitiveFocus.row) != null ? `${pickEmergingG5Exp(competitiveFocus.row)}%` : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Data date</dt>
                  <dd className="text-right text-xs text-slate-400">
                    {(competitiveFocus.row.data_date && String(competitiveFocus.row.data_date)) || '—'}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          <div
            className="rounded-2xl border p-5 space-y-3"
            style={{ background: COL.card, borderColor: COL.border }}
          >
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-200/90">AI analyst</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              Ask questions about this indication, benchmarks, and safety. Context can include the competitive
              landscape and the matched emerging report.
            </p>
            <Link
              to="/chatbot"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-violet-500"
            >
              <MessageSquare className="h-4 w-4" />
              Ask another question
            </Link>
          </div>

          <div className="rounded-2xl border p-4 space-y-2" style={{ background: COL.card, borderColor: COL.border }}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-200/90">FDA — similar</h3>
            <div className="max-h-48 overflow-y-auto text-[11px] space-y-2">
              {fdaDisplay.length === 0 ? (
                <p className="text-slate-500">No scored FDA rows.</p>
              ) : (
                fdaDisplay.slice(0, 6).map(({ row, score }, i) => (
                  <div key={String(row.message_id ?? i)} className="border-b border-white/5 pb-2 text-slate-300">
                    <div className="font-medium text-slate-100 line-clamp-2">{fdaLabel(row)}</div>
                    <div className="text-slate-500 mt-0.5">
                      Match {score}
                      {extractOrrFromFda(row) != null ? ` · ORR ~${extractOrrFromFda(row)}%` : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
            <Link to="/fda-approvals" className="inline-block text-[10px] font-semibold text-amber-200/90 hover:underline">
              Full FDA table →
            </Link>
          </div>

        </aside>

        <div className="xl:col-span-8 space-y-5 order-1 xl:order-2">
          <div className="rounded-2xl border p-5" style={{ background: COL.card, borderColor: COL.border }}>
            <div className="flex items-center justify-between gap-3 mb-1">
              <h3 className="text-xs font-bold uppercase tracking-widest text-amber-200/90">
                {benchMetric === 'orr' ? 'ORR' : benchMetric === 'mpfs' ? 'mPFS' : 'mOS'} benchmark vs SoC
              </h3>
              <div className="flex gap-1">
                {(['orr', 'mpfs', 'mos'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => selectBenchMetric(m)}
                    className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase transition-colors ${
                      benchMetric === m
                        ? 'bg-amber-500/30 text-amber-200 border border-amber-400/40'
                        : 'text-slate-500 hover:text-slate-300 border border-white/10'
                    }`}
                  >
                    {m === 'orr' ? 'ORR' : m === 'mpfs' ? 'mPFS' : 'mOS'}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mb-4">
              ORR / mPFS / mOS toggles sort the table by that column (descending, missing values last). Bars follow
              that order for rows with a value for the selected endpoint, then similar FDA comparators when ORR is
              selected. Click a bar for Asset focus.
            </p>
            {orrBenchmark.length === 0 ? (
              <p className="text-sm text-slate-400">No {benchMetric.toUpperCase()} values to chart for this selection.</p>
            ) : (
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={orrBenchmark} margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                    <XAxis type="number" domain={[0, 'auto']} tick={{ fill: '#94a3b8', fontSize: 11 }} unit={benchMetric === 'orr' ? '%' : ' mo'} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={132}
                      tick={{ fill: COL.gold, fontSize: 10 }}
                      interval={0}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      contentStyle={{
                        background: '#1e1b4b',
                        border: '1px solid rgba(139,92,246,0.4)',
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: '#ffffff' }}
                      itemStyle={{ color: '#ffffff' }}
                      formatter={(v: number | undefined) => [
                        benchMetric === 'orr' ? `${v ?? ''}%` : `${v ?? ''} mo`,
                        benchMetric === 'orr' ? 'ORR' : benchMetric === 'mpfs' ? 'mPFS' : 'mOS',
                      ]}
                    />
                    <Bar
                      dataKey="orr"
                      radius={[0, 6, 6, 0]}
                      barSize={16}
                      onClick={onBenchmarkBarClick}
                      cursor="pointer"
                    >
                      {orrBenchmark.map((entry) => (
                        <Cell
                          key={entry.key}
                          fill={entry.fill}
                          stroke={competitiveFocusKey === entry.key ? '#fde68a' : 'transparent'}
                          strokeWidth={competitiveFocusKey === entry.key ? 2 : 0}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: COL.soc }} />
                SoC DB
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: COL.fda }} />
                FDA
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: COL.emerging }} />
                Emerging
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: COL.nct }} />
                NCT
              </span>
            </div>
          </div>

          <div className="rounded-2xl border p-5" style={{ background: COL.card, borderColor: COL.border }}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-200/90 mb-1">Competitive landscape</h3>
            <p className="text-[11px] text-slate-500 mb-3">
              SoC comparators and emerging programs; click column headers to sort. Rows and bars stay aligned by sort
              order for the active benchmark endpoint.
            </p>
            <div className="max-h-[min(62vh,720px)] overflow-y-auto overflow-x-auto rounded-xl border border-white/5">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-[1] bg-[#1a1535]">
                  <tr className="text-left border-b border-white/10 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {(
                      [
                        ['program', 'Type'],
                        ['asset', 'Asset'],
                        ['phase', 'Ph'],
                        ['orr', 'ORR'],
                        ['mpfs', 'mPFS'],
                        ['mos', 'mOS'],
                        ['g35', 'G3-5%'],
                        ['g5', 'G5%'],
                        ['src', 'Src'],
                      ] as const
                    ).map(([col, label]) => {
                      const active = competitiveSort.col === col;
                      const mark = active ? (competitiveSort.dir === 'asc' ? '▲' : '▼') : '';
                      return (
                        <th key={col} className="py-2.5 px-3">
                          <button
                            type="button"
                            onClick={() => toggleCompetitiveSort(col)}
                            className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-0.5 -mx-0.5 text-left transition-colors hover:text-slate-200 ${
                              active ? 'text-amber-200/90' : ''
                            }`}
                          >
                            {label}
                            <span className="font-mono text-[9px] text-slate-500 tabular-nums w-3 shrink-0">
                              {active ? mark : '↕'}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {competitiveRows.map((r) => {
                    const selected = competitiveFocusKey === r.key;
                    return (
                      <tr
                        key={r.key}
                        onClick={() => setCompetitiveFocusKey(r.key)}
                        className={`cursor-pointer border-b border-white/5 text-slate-200 transition-colors hover:bg-white/[0.06] ${
                          selected ? 'bg-amber-500/10 ring-1 ring-inset ring-amber-400/35' : ''
                        }`}
                      >
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              r.program === 'soc'
                                ? 'bg-violet-500/25 text-violet-100'
                                : 'bg-amber-500/20 text-amber-100'
                            }`}
                          >
                            {r.program === 'soc' ? 'SoC' : 'Emerging'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 max-w-[min(28vw,220px)]">
                          <span className="line-clamp-2 font-medium">{r.asset}</span>
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap text-slate-400">{r.phase}</td>
                        <td className="py-2.5 px-3 font-mono tabular-nums">{r.orr}</td>
                        <td className="py-2.5 px-3 font-mono text-slate-400 tabular-nums">{r.mpfs}</td>
                        <td className="py-2.5 px-3 font-mono text-slate-400 tabular-nums">{r.mos}</td>
                        <td className="py-2.5 px-3 font-mono tabular-nums">{r.g35}</td>
                        <td className="py-2.5 px-3 font-mono tabular-nums">{r.g5}</td>
                        <td className="py-2.5 px-3">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              r.srcTone === 'nct'
                                ? 'bg-indigo-500/25 text-indigo-100'
                                : r.srcTone === 'soc'
                                  ? 'bg-violet-500/20 text-violet-100'
                                  : 'bg-amber-500/20 text-amber-100'
                            }`}
                          >
                            {r.src}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border p-5" style={{ background: COL.card, borderColor: COL.border }}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-amber-200/90 mb-3">
              Safety flags
            </h3>
            {safetyRows.g35.length === 0 && safetyRows.g5.length === 0 ? (
              <p className="text-sm text-slate-400">No G3–5 or G5/death fields found for this indication.</p>
            ) : (
              <div className="space-y-4 text-xs">
                {safetyRows.g35.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">G3–5 AEs (exp) %</p>
                    <div className="space-y-1">
                      {safetyRows.g35.map((r) => (
                        <div key={r.label} className="flex items-center justify-between gap-2">
                          <span className="text-slate-300 truncate max-w-[160px]">{r.label}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="tabular-nums text-slate-200 font-medium">{r.val.toFixed(1)}%</span>
                            <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold ${r.signal === 'FLAG' ? 'bg-rose-500/20 text-rose-300' : r.signal === 'WATCH' ? 'bg-amber-500/20 text-amber-200' : 'bg-emerald-500/15 text-emerald-300'}`}>
                              {r.signal}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {safetyRows.g5.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">G5 / death (exp) %</p>
                    <div className="space-y-1">
                      {safetyRows.g5.map((r) => (
                        <div key={r.label} className="flex items-center justify-between gap-2">
                          <span className="text-slate-300 truncate max-w-[160px]">{r.label}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="tabular-nums text-slate-200 font-medium">{r.val.toFixed(1)}%</span>
                            <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold ${r.signal === 'FLAG' ? 'bg-rose-500/20 text-rose-300' : r.signal === 'WATCH' ? 'bg-amber-500/20 text-amber-200' : 'bg-emerald-500/15 text-emerald-300'}`}>
                              {r.signal}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={resetForm}
            className="w-full rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/20 to-violet-600/20 py-3 text-sm font-bold text-amber-100 hover:from-amber-500/30 hover:to-violet-600/30"
          >
            Modify your dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const resumeCancerLabel =
    resumePrompt &&
    cancerTypes.find((c) => c.id === resumePrompt.saved.cancer_type_id)?.name;

  const inner = (
    <div id="clinical-overview" className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 py-6 space-y-8">
      {syncUrlAllowed && resumePrompt && step === 'form' && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clinical-resume-title"
        >
          <div
            className="max-w-md w-full rounded-2xl border p-6 shadow-2xl space-y-4"
            style={{ background: COL.card, borderColor: COL.border }}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-amber-500/15 p-2 text-amber-200">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <h2 id="clinical-resume-title" className="text-base font-bold text-white leading-snug">
                  Continue where you left off?
                </h2>
                <p className="text-sm text-slate-400 leading-relaxed">
                  We saved your last clinical overview. You can reopen that same SoC context and emerging match, or
                  start a new search.
                </p>
              </div>
            </div>
            <div
              className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-200 space-y-1.5"
              style={{ borderColor: COL.border }}
            >
              <p className="font-semibold text-amber-100/95 truncate">{resumePrompt.label}</p>
              {resumeCancerLabel ? (
                <p className="text-xs text-slate-500">{resumeCancerLabel}</p>
              ) : null}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-1">
              <button
                type="button"
                onClick={handleResumeNewSearch}
                className="order-2 sm:order-1 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-slate-200 hover:bg-white/5"
              >
                New search
              </button>
              <button
                type="button"
                onClick={handleResumeContinue}
                className="order-1 sm:order-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-2.5 text-sm font-bold text-slate-950 hover:from-amber-400 hover:to-amber-500"
              >
                Continue with this indication
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'form' && (
        <div className="space-y-4">
          <div className="text-center space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-200/80">Ruby Oncology Insights</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Clinical intelligence dashboard</h1>
            <p className="text-sm text-violet-200/70 max-w-lg mx-auto">
              Select a Standard of Care indication to load benchmarks, safety context, and FDA matches in a single
              dark overview.
            </p>
          </div>
          {formBlock}
        </div>
      )}

      {step === 'results' && (
        <>
          {loading ? (
            <p className="text-sm text-violet-200 flex items-center justify-center gap-2 py-20">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading dashboard…
            </p>
          ) : (
            resultsView
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return inner;
  }

  return (
    <div className="min-h-screen bg-[#0c0a1a] text-slate-100 pb-16">
      <Header />
      {inner}
    </div>
  );
};

export default IntegratedIndicationDashboard;
